import * as React from 'react'
import { FC, useEffect } from 'react'
import { useFormikContext } from 'formik'
import { SideDrawerForm } from '@sales-center/components'

import { DetailsWrapper } from './TemplateDetails/DetailsWrapper'
import { TemplatePreviewPopover } from './TemplateDetails/Preview/PreviewPopover'
import { useFormFieldActions } from './hooks'
import { ENTITY_TYPES } from '@sales-center/common'

import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { useAppDispatch } from '@user/store/hooks'
import { getOpportunityDetail } from '@user/store/opportunity'
import { getLeadDetail } from '@user/store/lead'
import { CreateAgreementFormModel } from '../typings'

import { Select } from '@workwave-tidal/core/components'
import FormHelperText from '@mui/material/FormHelperText'
import { ITemplate } from '@user/store/agreements'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { Theme } from '@mui/material/styles'

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  onCancel: () => void
  openServicesDrawer: () => void
  openCreateServiceDrawer: () => void
  externalLocationId: string
}

export const CreateAgreementFormFields: FC<Props> = (props) => {
  const {
    entityId,
    entityType,
    onCancel,
    openServicesDrawer,
    openCreateServiceDrawer,
    externalLocationId,
  } = props
  const tidalThemeActive = useTidalThemeActive()

  const dispatch = useAppDispatch()
  const { setErrors } = useFormikContext<CreateAgreementFormModel>()

  const {
    templates,
    submitForm,
    disableSave,
    handleTemplateChange,
    isValidating,
    handleTemplateClear,
    showPreview,
    setShowPreview,
    handleSelectOpen,
    handleSelectClose,
    templateId,
    templateError,
    templateError: { exceedsServiceLocations, exceedsServiceSlots },
    value,
    errors,
    ...details
  } = useFormFieldActions(entityId, entityType)

  useEffect(() => {
    if (entityType === ENTITY_TYPES.Opportunity) {
      dispatch(getOpportunityDetail(entityId))
    } else {
      dispatch(getLeadDetail(entityId))
    }
  }, [dispatch, entityType, entityId])

  useEffect(() => {
    if (!exceedsServiceLocations && !exceedsServiceSlots) {
      if (externalLocationId) {
        setErrors({})
      }
    }
  }, [
    externalLocationId,
    exceedsServiceLocations,
    exceedsServiceSlots,
    setErrors,
  ])

  const height = tidalThemeActive ? '65px' : '45px'

  return (
    <SideDrawerForm
      onSave={submitForm}
      primaryBtnLabel="Launch Form to Complete"
      disableSave={disableSave}
      onCancel={onCancel}
    >
      <Box p={2}>
        <Box
          display="flex"
          sx={{ marginBottom: (theme: Theme) => theme.spacing(1.6) }}
          alignItems="center"
        >
          <Typography variant="h7" sx={{ fontWeight: '600' }}>
            Select Agreement Template
          </Typography>
        </Box>

        <Box id={'asd'}>
          <Select<ITemplate>
            disabled={false}
            getLabel={(option) => option.templateFormName}
            items={templates?.sort()}
            onChange={handleTemplateChange}
            value={value ?? null}
            testId="selectAgreementTemplate"
            data-test-id="selectAgreementTemplate"
            error={errors.templateId ? true : false}
            required
            disableClearOnBlur
            label={'Select Agreement Template'}
            listMode="normal"
            autocompleteProps={{
              onOpen: () => handleSelectOpen(),
              onClose: () => handleSelectClose(),
            }}
            optionMode={'custom'}
            renderOption={(props, template: ITemplate) => (
              <MenuItem
                sx={{
                  height: height,
                  minHeight: height,
                  position: 'relative',
                }}
                {...props}
                key={template.templateId}
                value={template.templateId}
              >
                <Box
                  display="flex"
                  justifyContent={'space-between'}
                  flexGrow={1}
                >
                  <Typography display="inline" variant="subtitle1">
                    {template.templateFormName}
                  </Typography>
                  {showPreview && (
                    <TemplatePreviewPopover templateId={template.templateId} />
                  )}
                </Box>
              </MenuItem>
            )}
          />
          {errors.templateId && (
            <FormHelperText sx={{ ml: 1 }} error>
              {errors.templateId}
            </FormHelperText>
          )}
        </Box>
        <DetailsWrapper
          {...details}
          templateError={templateError}
          templateId={templateId}
          entityId={entityId}
          entityType={entityType}
          openServicesDrawer={openServicesDrawer}
          openCreateServiceDrawer={openCreateServiceDrawer}
        />
      </Box>
    </SideDrawerForm>
  )
}
CreateAgreementFormFields.displayName = 'CreateAgreementFormFields'
